/**
 * Materials Review. Horizontal tabs on top, one section shown: Submit,
 * My materials, Review queue, Library, AI accuracy, Settings. House layout
 * (Audit and Issues Log), lighthouse mark, navy and one orange, no emoji.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api, materialFromUrl, setMaterialInUrl } from './api.js';
import { TabBar, Button } from './ui.jsx';
import SubmitTab from './SubmitTab.jsx';
import MaterialsTab from './MaterialsTab.jsx';
import MaterialPage from './MaterialPage.jsx';
import LibraryTab from './LibraryTab.jsx';
import AccuracyTab from './AccuracyTab.jsx';
import SettingsTab from './SettingsTab.jsx';

export default function MaterialsReviewApp({ user }) {
  const [boot, setBoot] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('submit');
  const [openId, setOpenIdState] = useState(() => materialFromUrl()); // the open material lives in the address, so a reload comes back to it
  const [openFile, setOpenFile] = useState(null); // the file just chosen on Submit, drawn before the server has read it
  const [refreshKey, setRefreshKey] = useState(0);
  const setOpenId = (id) => { setMaterialInUrl(id); setOpenIdState(id); };

  const load = useCallback(async () => {
    const d = await api('/bootstrap');
    if (!d?.success) { setErr(d?.message || 'Could not load'); return; }
    setBoot(d);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  if (err) return <div className="p-10 text-sm text-rose-600">Materials Review could not load: {err}</div>;
  if (!boot) return <div className="p-10 text-sm text-slate-400">Loading Materials Review…</div>;

  const counts = boot.counts || {};
  const tabs = [
    ['submit', 'Submit'],
    ['materials', 'My materials', (counts.draft || 0) + (counts.returned || 0)],
    ...(boot.is_compliance ? [['queue', 'Review queue', boot.review_queue_count]] : []),
    ['library', 'Library'],
    ...(boot.is_compliance ? [['accuracy', 'AI accuracy'], ['settings', 'Settings']] : []),
  ];

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Materials Review</h1>
              <p className="text-sm text-slate-500 mt-0.5">Marketing and communications materials from draft to approved, submitted and in use.{boot.org_name ? ` Plan: ${boot.org_name}.` : ''}</p>
            </div>
          </div>
          {!boot.settings?.exists && boot.is_compliance && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 max-w-sm">Plan setup is not done. Open Settings and enter the contract, counties, languages, the material ID pattern and the approvers before the first material.</div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <TabBar value={openId ? 'material' : tab} onChange={(v) => { setOpenId(null); setOpenFile(null); setTab(v); }} tabs={openId ? [...tabs, ['material', 'Material']] : tabs} />
          {openId && <Button kind="secondary" onClick={() => { setOpenId(null); setOpenFile(null); refresh(); }}>Back to list</Button>}
        </div>

        <div className="mt-6">
          {openId ? (
            <MaterialPage key={openId} id={openId} boot={boot} localFile={openFile} onChanged={refresh} onClose={() => { setOpenId(null); setOpenFile(null); refresh(); }} />
          ) : tab === 'submit' ? (
            <SubmitTab boot={boot} onCreated={(id, file) => { refresh(); setOpenFile(file || null); setOpenId(id); }} />
          ) : tab === 'materials' ? (
            <MaterialsTab boot={boot} mode="mine" onOpen={(id) => { setOpenFile(null); setOpenId(id); }} refreshKey={refreshKey} />
          ) : tab === 'queue' ? (
            <MaterialsTab boot={boot} mode="queue" onOpen={(id) => { setOpenFile(null); setOpenId(id); }} refreshKey={refreshKey} />
          ) : tab === 'library' ? (
            <LibraryTab boot={boot} onOpen={(id) => { setOpenFile(null); setOpenId(id); }} refreshKey={refreshKey} />
          ) : tab === 'accuracy' ? (
            <AccuracyTab boot={boot} />
          ) : (
            <SettingsTab onSaved={refresh} />
          )}
        </div>
      </div>
    </div>
  );
}
